import { cn } from "@gigpro/ui/tailwind/cn";
import { Command, useCommandState } from "cmdk";
import React from "react";
import { useIsFocused } from "../../utils/useIsFocused";
import { Input, type InputProps } from "../Input";
import { LinearProgress } from "../LinearProgress";
import { Popover, PopoverContent, PopoverTrigger } from "../Popover";

export interface ComboboxProps extends Omit<InputProps, "value" | "asChild"> {
  loading?: boolean;
  children: React.ReactNode;
  value?: string;
  onValueChange: (value: string) => void;
  shouldFilter?: boolean;
  label?: string;
}

export const Combobox = ({
  ref,
  children,
  loading,
  value,
  onValueChange,
  shouldFilter,
  label,
  ...inputProps
}: ComboboxProps) => {
  const [open, setOpen] = React.useState(false);
  const { isFocused, ...focusHandlers } = useIsFocused({
    onFocus: inputProps.onFocus,
    onBlur: (e) => {
      setOpen(false);
      inputProps.onBlur?.(e);
    },
  });
  return (
    <Popover open={open}>
      <Command
        label={label}
        shouldFilter={shouldFilter}
        onKeyUp={(e) => {
          // close if item is selected using enter
          if (e.key === "Enter") setOpen(false);
        }}
      >
        <PopoverTrigger asChild>
          <Input {...inputProps} asChild>
            <Command.Input
              ref={ref}
              value={value}
              onValueChange={(value) => {
                setOpen(isFocused && !!value);
                onValueChange(value);
              }}
              {...focusHandlers}
            />
          </Input>
        </PopoverTrigger>
        <Command.List aria-hidden={!open}>
          <PopoverContent
            className="w-(--radix-popover-trigger-width)"
            avoidCollisions={false}
            onOpenAutoFocus={(e) => e.preventDefault()}
          >
            <LinearProgress className={cn("transition-all duration-300", !loading && "invisible -mt-0.5 opacity-0")} />
            {!loading && <Command.Empty className="p-1 text-large">No results found.</Command.Empty>}
            {children}
          </PopoverContent>
        </Command.List>
      </Command>
    </Popover>
  );
};
Combobox.displayName = "Combobox";

export const ComboboxItem = ({
  ref,
  className,
  ...props
}: React.ComponentProps<typeof Command.Item> & {
  ref?: React.Ref<HTMLDivElement>;
}) => (
  <Command.Item
    ref={ref}
    className={cn(
      "relative flex w-full cursor-pointer items-center rounded-sm px-2 py-1.5 text-large outline-none select-none focus:bg-background focus:text-black data-[disabled=true]:pointer-events-none data-[disabled=true]:opacity-50 data-[selected=true]:bg-background data-[selected=true]:font-semibold",
      className,
    )}
    {...props}
  />
);
ComboboxItem.displayName = "ComboboxItem";

export const ComboboxSubItem = ({
  ref,
  ...props
}: React.ComponentProps<typeof ComboboxItem> & {
  ref?: React.Ref<HTMLDivElement>;
}) => {
  const search = useCommandState((state) => state.search);
  if (!search) return null;
  return <ComboboxItem ref={ref} {...props} />;
};

export { Command } from "cmdk";
